'use client';
// VX-2 X1 — 「지금(위성) ↔ 완공 상상도」 비교 슬라이더.
//
// ── 폴백이 먼저다 ──
//   SSR 마크업은 두 <figure> 를 «세로로 쌓은» 평범한 그림 두 장이다(JS 없이도 둘 다 보인다 · 크롤러가 읽는 것도 이 모양).
//   수화되면 루트에 data-enhanced 를 붙이고, 그때만 겹침 CSS(rd2-detail.css `.kd-cmp[data-enhanced]`)가 걸린다.
//
// ── §N · G-VX1 ──
//   · 두 장 모두 SSR <img> · loading="lazy"(히어로가 LCP 다 — 여기서 먼저 받지 않는다) · width/height 속성(CLS 0).
//   · 상상도: <img src> = master JPEG 절대 URL(색인 대상), <source> = 표시용 webp. 자기 <figure> + 「AI 생성」 고지 figcaption.
//   · 위성: /img-sat/… (자체 도메인 · noindex 헤더). alt 는 상상도와 다르다.
//   ⛔ Storage 직링크를 props 로 받지 않는다 — 판정·주소 변환은 lib/site-image/compare.ts(서버)에서 끝난다.
//
// ── 조작 ──
//   포인터(마우스·터치·펜) 드래그 · 키보드(←→ 5 · PageUp/Down 20 · Home/End). role="slider" 는 손잡이 하나.
//   touch-action: pan-y — 세로 스크롤은 브라우저가 그대로 한다(가로 끌기만 가로챈다).
// ── 계측 ──
//   사용자가 위치를 «실제로 바꾼» 첫 1회에 compare_slider_use 를 1번만 기록(lib/analytics track · user_events props · 스키마 변경 0).

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { trackFeature } from '@/lib/analytics';
import { clampComparePos, compareKeyStep, COMPARE_CAPTION_BASE, type CompareSliderData } from '@/lib/site-image/compare';
import VworldCredit from '@/components/attribution/VworldCredit';
import { safeDuoSrc } from '@/lib/apt/detail-vx3';

/**
 * VX-3 R5 — `duoSrc`(선택): 「지금 → 완공」 2단 이미지(레인 A 의 duo 변형본 · 자체 도메인 /img/…jpg).
 *   오면 noscript 요소 안에 그 한 장을 낸다 — JS 가 꺼진 브라우저는 CSS `@media (scripting: none)` 로 세로 쌓기를 접고 2단 그림만 본다.
 *   안 오거나 주소가 자체 도메인 JPEG 가 아니면 종전 그대로(두 figure 세로 쌓기). SSR 의 두 <img>(색인 대상)는 어느 쪽이든 그대로 남는다.
 */
export default function CompareSlider({ data, siteSlug, duoSrc }: { data: CompareSliderData; siteSlug?: string | null; duoSrc?: string }) {
  const duo = safeDuoSrc(duoSrc);
  const [enhanced, setEnhanced] = useState(false);
  const [pos, setPos] = useState(50);
  const stageRef = useRef<HTMLDivElement | null>(null);
  const dragging = useRef(false);
  const used = useRef(false);

  useEffect(() => { setEnhanced(true); }, []);

  const move = useCallback((next: number, via: 'pointer' | 'key') => {
    const v = clampComparePos(next);
    setPos((prev) => {
      if (v !== prev && !used.current) {
        used.current = true;
        try { trackFeature('compare_slider_use', { site_slug: siteSlug ?? null, shot: data.shot, via }); } catch { /* 계측 실패는 화면을 막지 않는다 */ }
      }
      return v;
    });
  }, [data.shot, siteSlug]);

  const posFromClientX = (clientX: number): number | null => {
    const el = stageRef.current;
    if (!el) return null;
    const r = el.getBoundingClientRect();
    if (r.width <= 0) return null;
    return ((clientX - r.left) / r.width) * 100;
  };

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!enhanced || (e.pointerType === 'mouse' && e.button !== 0)) return;
    dragging.current = true;
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* 일부 브라우저 — 캡처 없이도 동작 */ }
    const p = posFromClientX(e.clientX);
    if (p != null) move(p, 'pointer');
  };
  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragging.current) return;
    const p = posFromClientX(e.clientX);
    if (p != null) move(p, 'pointer');
  };
  const endDrag = (e: React.PointerEvent<HTMLDivElement>) => {
    dragging.current = false;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* 이미 풀림 */ }
  };
  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const next = compareKeyStep(e.key, pos);
    if (next == null) return;
    e.preventDefault();
    move(next, 'key');
  };

  const rounded = Math.round(pos);
  return (
    <div className="kd-cmp" data-vx="compare" data-enhanced={enhanced ? '' : undefined} data-duo={duo ? '' : undefined}>
      <div
        ref={stageRef}
        className="kd-cmp__stage"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
      >
        <figure className="kd-cmp__fig kd-cmp__fig--before" style={enhanced ? { clipPath: `inset(0 ${100 - pos}% 0 0)` } : undefined}>
          {/* eslint-disable-next-line @next/next/no-img-element -- /img-sat rewrite(자체 도메인 · noindex). next/image 는 주소를 /_next/image 로 바꾼다. */}
          <img src={data.before.src} alt={data.before.alt} width={data.before.width} height={data.before.height} loading="lazy" decoding="async" draggable={false} />
          <figcaption className="kd-cmp__tag" data-rd="label"><b>지금</b><span className="kd-cmp__more"> · 현재 위성사진</span></figcaption>
        </figure>
        <figure className="kd-cmp__fig kd-cmp__fig--after">
          <picture>
            {data.after.webp ? <source type="image/webp" srcSet={data.after.webp} /> : null}
            {/* eslint-disable-next-line @next/next/no-img-element -- §N-1: 색인 대상은 SSR <img src> 절대 URL(자체 도메인 master JPEG). */}
            <img src={data.after.src} alt={data.after.alt} width={data.after.width} height={data.after.height} loading="lazy" decoding="async" draggable={false} />
          </picture>
          <figcaption className="kd-cmp__tag" data-rd="label"><b>완공 상상도</b><span className="kd-cmp__more"> · AI 생성 이미지로 실제와 다를 수 있음</span></figcaption>
        </figure>
        {enhanced ? (
          <div
            className="kd-cmp__handle"
            role="slider"
            tabIndex={0}
            aria-label="지금 위성사진과 완공 상상도 비교 — 좌우 화살표로 조절"
            aria-orientation="horizontal"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={rounded}
            aria-valuetext={`지금 ${rounded}% · 완공 상상도 ${100 - rounded}%`}
            style={{ left: `${pos}%` }}
            onKeyDown={onKeyDown}
          >
            <span className="kd-cmp__grip" aria-hidden="true" />
          </div>
        ) : null}
      </div>
      {duo ? (
        <noscript>
          <figure className="kd-cmp__duo">
            {/* eslint-disable-next-line @next/next/no-img-element -- JS 꺼짐 폴백 한 장(자체 도메인 duo 변형본 JPEG). */}
            <img src={duo} alt={`${data.before.alt}과 완공 AI 상상도 비교`} width={1200} height={630} loading="lazy" decoding="async" />
            <figcaption className="kd-cmp__duo-cap">지금(위성사진) · 완공 AI 상상도 — AI 생성 이미지로 실제와 다를 수 있음 · <VworldCredit /></figcaption>
          </figure>
        </noscript>
      ) : null}
      <p className="kd-cmp__cap">{COMPARE_CAPTION_BASE} · <VworldCredit /></p>
    </div>
  );
}
